/**
 * CW-SF-D1 TKT-5 — Pipeline by Owner widget.
 *
 * Bar chart of sum open Amount per OwnerId, descending. Same SVG
 * primitive as Pipeline by Stage; bars labelled by rep name. Reps
 * the viewer can't see (no opps in their visibility) don't appear.
 */
import { useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface OwnerBucket {
  owner_id: string;
  owner_name: string | null;
  total: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  owners: OwnerBucket[];
  amount_denied: boolean;
  total_visible: number;
}

const REFRESH_S = 5 * 60;

export function SfPipelineByOwnerWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-pipeline-by-owner', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const owners = data?.owners ?? [];
  const maxTotal = owners.length ? Math.max(...owners.map((b) => b.total)) : 0;

  return (
    <div className="sf-widget sf-widget--pipeline">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={owners.length > 0}
        emptyMessage={
          data?.amount_denied
            ? "Your Salesforce profile doesn't have access to Opportunity Amount."
            : 'No open team pipeline visible to you.'
        }
      >
        <ul className="sf-widget__bars" aria-label="Pipeline by owner">
          {owners.map((b) => {
            const pct = maxTotal ? (b.total / maxTotal) * 100 : 0;
            const label = b.owner_name ?? (b.owner_id === '(unassigned)' ? 'Unassigned' : '(unknown rep)');
            return (
              <li key={b.owner_id} className="sf-widget__bar">
                <div className="sf-widget__bar-label">
                  <span>{label}</span>
                  <span className="sf-widget__bar-meta">
                    {formatCurrency(b.total)} · {b.count}
                  </span>
                </div>
                <svg
                  className="sf-widget__bar-track"
                  viewBox="0 0 100 6"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={`${label}: ${formatCurrency(b.total)}`}
                >
                  <rect x="0" y="0" width="100" height="6" rx="2" className="sf-widget__bar-bg" />
                  <rect x="0" y="0" width={pct} height="6" rx="2" className="sf-widget__bar-fill" />
                </svg>
              </li>
            );
          })}
        </ul>
        {data && data.total_visible > 0 && (
          <div className="sf-widget__footer-meta">
            Visible to you: {formatCurrency(data.total_visible)}
          </div>
        )}
      </SfWidgetShell>
    </div>
  );
}
